<template>
	<HeroLayout :show-contact-us-text=false>
		<div class="flex flex-col w-full h-full">
			<img :src="logoLargePath" alt="Logo" class="registration-title-image" />
			<p class="registration-title">Sign up for an account</p>
			<p class="registration-subtitle mt-1 mb-10">Already have an account?
				<router-link :to="{ name: 'auth.login' }" class="clickable-text">Log in</router-link>
				.
			</p>
			<form>
				<InputText label="Email address*" type="email" autocomplete="email" placeholder="Enter your email address"
							  v-model="email" :errors="error" error-field="user.email" disabled />

				<div class="flex relative mt-8">
					<InputText label="Create password*" :type="hidePassword ? 'password' : 'text'" autocomplete="current-password"
								  placeholder="Enter password" v-model="password" :icon="hidePassword ? InputIcon.PASSWORD_LOCKED : InputIcon.PASSWORD_UNLOCKED"
								  :errors="error" error-field="user.password"
								  tooltip="Your password must be at least 8 characters and contain one symbol and one number." class="userpilot-mask w-full" />
					<p class="registration-password-toggle" v-if="hidePassword" @click.prevent="hidePassword = false">Show password</p>
					<p class="registration-password-toggle" v-if="!hidePassword" @click.prevent="hidePassword = true">Hide password</p>
				</div>

				<InputText class="userpilot-mask mt-4 text-black" label="Confirm password*" :type="hidePassword ? 'password' : 'text'" autocomplete="current-password"
							  placeholder="Re-enter password" v-model="password_confirmation"
							  :icon="hidePassword ? InputIcon.PASSWORD_LOCKED : InputIcon.PASSWORD_UNLOCKED" />

				<div class="flex flex-row items-center pt-6">
					<check-box v-model="t_and_c_checkbox" :has-error="hasTandCError" />
					<div class="text-grayscale-body ml-3 cursor-default">I agree to the <span class="clickable-text" @click="showTerms=true">Terms and Conditions, and Privacy</span>.*</div>
				</div>
				<ValidationError :errors="error" field="user.t_and_c" class="mt-2 ml-3.5" />

				<SubmitButton class="ml-auto mt-16" label="Sign Up" :loading="isLoading" @click="register" />
				<ValidationError :errors="error" :field="null" />
			</form>
		</div>
	</HeroLayout>
	<Teleport to="html">
		<TermsPrivacyModal :is-open="showTerms" @close="showTerms = false" />
	</Teleport>
</template>

<script setup lang="js">
import { computed, onMounted, ref } from 'vue'
import InputText from '@/components/Forms/InputText.vue'
import ValidationError from '@/components/Forms/InputError.vue'
import CheckBox from '@/components/Forms/CheckBox.vue'
import HeroLayout from '@/layouts/HeroLayout.vue'
import router from '@/router/router.js'
import { userApi } from '@/api/User/UserApi.js'
import { InputIcon } from '@/models/enums/InputIcon.js'
import SubmitButton from '@/components/Forms/Buttons/SubmitButton.vue'
import { useRoute } from 'vue-router'
import { Tenants } from '@/models/tenants/Tenants.js'
import TermsPrivacyModal from '@/components/Modals/TermsPrivacyModal.vue'

const isLoading = ref(false)
const hidePassword = ref(true)
const error = ref(null)
const email = ref('')
const password = ref('')
const password_confirmation = ref('')
const t_and_c_checkbox = ref(false)
const route = useRoute()
const logoLargePath = ref('')
const showTerms = ref(false)

const hasTandCError = computed(() =>
{
	return error.value?.validationErrors().find(err => err.source === 'user.t_and_c') != null ?? false
})

onMounted(() =>
{
	email.value = route.query.email

	if (!route.query.token)
	{
		router.push({ name: 'auth.login' })
	}

	const tenant = Tenants.getTenant()
	logoLargePath.value = new URL(`../../assets/images/theme/${tenant.registrationLogo.image}`, import.meta.url).href
})

function register()
{
	const token = route.query.token

	isLoading.value = true
	userApi.register(
		email.value,
		password.value,
		password_confirmation.value,
		t_and_c_checkbox.value,
		token,
		(shouldNavigate) =>
		{
			isLoading.value = false
			if (shouldNavigate)
			{
				router.push({ name: 'auth.add-profile-info'})//'auth.before-we-continue'
			}
		},
		(e) =>
		{
			isLoading.value = false
			error.value = e.showErrorBanner()
		})
}

</script>
